import { type ChartConfig } from "@shared-components/shadcn/chart";
import { api } from "@shared-server/api";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@shared-components/shadcn/chart";
import {
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Legend,
  BarChart,
  Tooltip,
} from "recharts";
import { COLORS } from "@shared-components/core-metrics/metric-utils";
import { useDepartmentStackContext } from "@shared-components/department-stack-provider";
import { pluginsMetadata } from "@chat-ai/_database/models/plugins-metadata";
import { type PluginTypeEnumTypes } from "@chat-ai/_database/enums";

type PluginsUsageProps = {
  startDate: Date;
  endDate: Date;
};

const chartConfig: ChartConfig = {
  count: {
    label: "Plugins usage",
  },
};

const secondChartConfig: ChartConfig = {
  count: {
    label: "Plugin categories",
  },
};

const pluginCategorizedMappedLabels = {
  noPlugins: "0 plugins",
  onePlugin: "1 plugin",
  twoPlugins: "2 plugins",
  multiplePlugins: "3+ plugins",
} as const;

const pluginLabel = (type: PluginTypeEnumTypes) => {
  const label = pluginsMetadata(type).name;

  return label.split(" ").join("\n");
};

const pluginCategorizedLabel = (value: string) => {
  return (
    pluginCategorizedMappedLabels[
      value as keyof typeof pluginCategorizedMappedLabels
    ] || value
  );
};

export const PluginsUsage = (props: PluginsUsageProps) => {
  const { parentDepartmentId } = useDepartmentStackContext();

  const { data } = api.coreMetrics.pluginsUsageQuery.useQuery({
    dateStart: props.startDate,
    dateEnd: props.endDate,
    initialDepartmentId: parentDepartmentId,
  });

  const chartData = (data?.pluginSpecific.overall || []).map((d) => ({
    ...d,
    count: Number(d.count),
  }));
  const chartDataByDepartment = data?.pluginSpecific.byDepartment || [];
  const categorizedChatsData = (data?.categorizedChats.overall || []).map(
    (d) => ({
      ...d,
      count: Number(d.count),
    }),
  );
  const categorizedChatsDataByDepartment = (
    data?.categorizedChats.byDepartment || []
  ).map((d) => ({
    ...d,
    count: Number(d.count),
  }));

  const departments = data?.departments || [];

  return (
    <div>
      <h2 className="mb-4">{chartConfig.count?.label}</h2>
      <ChartContainer
        config={chartConfig}
        className="aspect-auto h-[150px] w-full"
      >
        <BarChart accessibilityLayer data={chartData}>
          <CartesianGrid vertical={false} />
          <XAxis
            dataKey="pluginType"
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            fill="#006e96"
            minTickGap={32}
            tickFormatter={(value: PluginTypeEnumTypes) => pluginLabel(value)}
          />
          <YAxis
            yAxisId="count"
            dataKey="count"
            interval={1}
            orientation="left"
          />
          <ChartTooltip
            content={
              <ChartTooltipContent
                className="w-[150px]"
                nameKey="pluginType"
                labelFormatter={(value: PluginTypeEnumTypes) =>
                  pluginLabel(value)
                }
              />
            }
          />
          <Legend />
          <Bar
            yAxisId="count"
            dataKey="count"
            className="fill-ter-blue-500"
            fill="#006e96"
          />
        </BarChart>
      </ChartContainer>

      <h2 className="mb-4 mt-8">{secondChartConfig.count?.label}</h2>
      <ChartContainer
        config={secondChartConfig}
        className="aspect-auto h-[150px] w-full"
      >
        <BarChart accessibilityLayer data={categorizedChatsData}>
          <CartesianGrid vertical={false} />
          <XAxis
            dataKey="category"
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            fill="#006e96"
            minTickGap={32}
            tickFormatter={(value: string) => pluginCategorizedLabel(value)}
          />
          <YAxis
            yAxisId="count"
            dataKey="count"
            interval={1}
            orientation="left"
          />
          <ChartTooltip
            content={
              <ChartTooltipContent
                className="w-[150px]"
                nameKey="category"
                labelFormatter={(value: string) =>
                  pluginCategorizedLabel(value)
                }
              />
            }
          />
          <Legend />
          <Bar
            yAxisId="count"
            dataKey="count"
            className="fill-ter-blue-500"
            fill="#006e96"
          />
        </BarChart>
      </ChartContainer>

      <div className="flex row gap-3 items-center mt-3">
        <h2 className="mb-4">{"Departments categorization"}</h2>
      </div>
      <h2 className="mb-4">{chartConfig.count?.label}</h2>
      <ChartContainer
        config={chartConfig}
        className="aspect-auto h-[150px] w-full"
      >
        <BarChart accessibilityLayer data={chartDataByDepartment}>
          <CartesianGrid />
          <XAxis
            dataKey="pluginType"
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            fill="#006e96"
            minTickGap={32}
            tickFormatter={(value: PluginTypeEnumTypes) => pluginLabel(value)}
          />
          <YAxis yAxisId="count" interval={1} orientation="left" />
          <ChartTooltip
            wrapperStyle={{ zIndex: 1 }}
            content={
              <ChartTooltipContent
                className="max-w-[350px]"
                itemsContainerClassName="grid grid-cols-3 gap-2"
                nameKey="departmentCode"
                labelFormatter={(value: PluginTypeEnumTypes) =>
                  pluginLabel(value)
                }
              />
            }
          />
          <Tooltip />
          <Legend />
          {departments.map((department, departmentIndex) => (
            <Bar
              key={department.id}
              yAxisId="count"
              dataKey={department.id}
              name={department.name}
              fill={COLORS[departmentIndex % COLORS.length]}
            />
          ))}
        </BarChart>
      </ChartContainer>

      <h2 className="mb-4 mt-8">{secondChartConfig.count?.label}</h2>
      <ChartContainer
        config={secondChartConfig}
        className="aspect-auto h-[150px] w-full"
      >
        <BarChart accessibilityLayer data={categorizedChatsDataByDepartment}>
          <CartesianGrid />
          <XAxis
            dataKey="category"
            tickLine={false}
            axisLine={false}
            tickMargin={8}
            fill="#006e96"
            minTickGap={32}
            tickFormatter={(value: string) => pluginCategorizedLabel(value)}
          />
          <YAxis yAxisId="count" interval={1} orientation="left" />
          <ChartTooltip
            wrapperStyle={{ zIndex: 1 }}
            content={
              <ChartTooltipContent
                className="max-w-[350px]"
                itemsContainerClassName="grid grid-cols-3 gap-2"
                nameKey="category"
                labelFormatter={(value: string) =>
                  pluginCategorizedLabel(value)
                }
              />
            }
          />
          <Legend />

          {departments.map((department, departmentIndex) => (
            <Bar
              key={department.id}
              yAxisId="count"
              dataKey={department.id}
              name={department.name}
              fill={COLORS[departmentIndex % COLORS.length]}
            />
          ))}
        </BarChart>
      </ChartContainer>
    </div>
  );
};
